<template>
  <iColumn gap="standard" :width="width" data-test="story-comment-thread">
    <iText v-if="thread.error" variant="error" data-test="story-comment-error">
      {{ thread.error }}
    </iText>
    <iRow
      v-if="recentBlock"
      gap="small"
      vertical-align="middle"
      width="fill"
      wrap="nowrap"
      data-test="story-comment-blocked-notice"
    >
      <iText variant="subtle" font-size="small" :style-overrides="subtleFontStyle">
        You blocked {{ recentBlock.name }}.
      </iText>
      <iButton
        variant="text"
        size="small"
        :style-overrides="BLOCK_NOTICE_LINK"
        data-test="story-comment-block-undo"
        @click="storyComments.undoRecentBlock()"
      >
        Undo
      </iButton>
      <iButton
        variant="text"
        size="small"
        icon-left="close"
        aria-label="Dismiss"
        :style-overrides="BLOCK_NOTICE_LINK"
        data-test="story-comment-block-dismiss"
        @click="storyComments.dismissRecentBlock()"
      />
    </iRow>
    <iText
      v-if="thread.loaded && thread.comments.length === 0"
      variant="subtle"
      :style-overrides="subtleFontStyle"
      data-test="story-comments-empty"
    >
      {{ canCommentOnPosts ? "No comments yet. Be the first to comment." : "No comments." }}
    </iText>
    <StoryCommentItem
      v-for="comment in thread.comments"
      :key="comment.id"
      :story-id="storyId"
      :comment="comment"
      :completions="completions"
      :font-style="fontStyle"
      :active-icon-color="activeIconColor"
      @reply="startReply(comment, $event)"
    >
      <iColumn
        v-if="comment.replies.length || comment.replyCount > 0 || replyingTo === comment.id"
        gap="small"
        width="fill"
        :style-overrides="{paddingLeft: 'gigantic'}"
        data-test="story-comment-replies"
      >
        <StoryCommentItem
          v-for="reply in comment.replies"
          :key="reply.id"
          :story-id="storyId"
          :comment="reply"
          :completions="completions"
          :font-style="fontStyle"
          :active-icon-color="activeIconColor"
          @reply="startReply(comment, $event)"
        />
        <iRow
          v-if="comment.replyCount > comment.replies.length && !repliesLoading(comment)"
          gap="standard"
          vertical-align="middle"
          wrap="nowrap"
        >
          <iColumn :width="REPLY_RULE_WIDTH">
            <iDivider color="light" />
          </iColumn>
          <iButton
            variant="text"
            size="small"
            :style-overrides="VIEW_REPLIES_LINK"
            data-test="story-comment-show-replies"
            @click="storyComments.loadReplies(storyId, comment)"
          >
            {{ replyToggleLabel(comment) }}
          </iButton>
        </iRow>
        <iLoadingSpinner v-if="repliesLoading(comment)" size="small" />
        <iColumn
          v-if="replyingTo === comment.id"
          gap="extraSmall"
          width="fill"
          data-test="story-comment-reply-composer"
        >
          <iCommentComposer
            :key="replyKey"
            :on-submit="segments => sendReply(comment, segments)"
            :initial-segments="replyPrefill"
            :completions="completions"
            :input-style-overrides="COMMENT_INPUT_STYLE"
            :send-variant="COMMENT_SEND_VARIANT"
            :send-style-overrides="COMMENT_SEND_STYLE"
            placeholder="Add a reply…"
            submit-label="Reply"
            show-cancel
            autofocus
            @cancel="stopReply"
          />
          <iText v-if="replyError" variant="error" font-size="small">
            {{ replyError }}
          </iText>
        </iColumn>
      </iColumn>
    </StoryCommentItem>
    <iLoadingSpinner v-if="thread.loading" />
    <iButton
      v-if="thread.hasMore && !thread.loading"
      variant="secondary"
      size="small"
      data-test="story-comments-load-more"
      @click="storyComments.loadComments(storyId, {more: true})"
    >
      Load more comments
    </iButton>
  </iColumn>
</template>

<script>
import { mapState } from "pinia";
import { emojiCompletion } from "@bloglovin/vue-component-library";
import { useConfigurationStore } from "%/dynamic-component-editor/stores/configuration-store";
import { useStoryCommentsStore } from "%/dynamic-component-editor/stores/story-comments-store";
import { SentCommentNotEchoedError, mentionCompletionFor, replyPrefillSegments } from "%/dynamic-component-editor/helpers/story-comments.mjs";
import StoryCommentItem from "%/dynamic-component-editor/components/story-comments/StoryCommentItem.vue";
import {
  COMMENT_INPUT_STYLE,
  COMMENT_SEND_STYLE,
  COMMENT_SEND_VARIANT,
} from "%/dynamic-component-editor/constants/comment-composer-style";

const REPLY_RULE_WIDTH = 24;
const VIEW_REPLIES_LINK = Object.freeze({
  smallPaddingTop: "none",
  smallPaddingRight: "none",
  smallPaddingBottom: "none",
  smallPaddingLeft: "none",
  textVariantFontColor: "subtle",
  textVariantHoverFontColor: "standard",
  textVariantFontWeight: "dark",
});
const BLOCK_NOTICE_LINK = Object.freeze({
  smallPaddingTop: "none",
  smallPaddingRight: "none",
  smallPaddingBottom: "none",
  smallPaddingLeft: "none",
  textVariantFontColor: "subtle",
  textVariantHoverFontColor: "standard",
  textVariantFontWeight: "dark",
});

export default {
  name: "StoryCommentThread",
  components: { StoryCommentItem },
  props: {
    storyId: {
      type: Number,
      required: true,
    },
    width: {
      type: String,
      default: "fill",
    },
    fontColor: {
      type: [String, Object],
      default: "",
    },
    replyInComposer: {
      type: Boolean,
      default: false,
    },
    activeIconColor: {
      type: String,
      default: "",
    },
  },
  emits: ["reply-requested"],
  data() {
    return {
      replyingTo: 0,
      replyPrefill: null,
      replyKey: 0,
      replyError: "",
      COMMENT_INPUT_STYLE,
      COMMENT_SEND_STYLE,
      COMMENT_SEND_VARIANT,
      REPLY_RULE_WIDTH,
      VIEW_REPLIES_LINK,
      BLOCK_NOTICE_LINK,
    };
  },
  computed: {
    ...mapState(useConfigurationStore, ["canCommentOnPosts"]),
    storyComments() {
      return useStoryCommentsStore();
    },
    thread() {
      return this.storyComments.thread(this.storyId);
    },
    recentBlock() {
      const block = this.storyComments.recentBlock;
      return block?.storyId === this.storyId ? block : null;
    },
    fontStyle() {
      return this.fontColor ? { standardFontColor: this.fontColor } : {};
    },
    subtleFontStyle() {
      return this.fontColor ? { subtleFontColor: this.fontColor } : {};
    },
    completions() {
      return [mentionCompletionFor(() => this.thread.comments), emojiCompletion];
    },
  },
  watch: {
    storyId: {
      immediate: true,
      handler(storyId) {
        if (storyId > 0 && !this.storyComments.thread(storyId).loaded) {
          this.storyComments.loadComments(storyId);
        }
      },
    },
  },
  methods: {
    repliesLoading(comment) {
      return Boolean(this.storyComments.replyPaging[comment.id]?.loading);
    },
    replyToggleLabel(comment) {
      const hidden = comment.replyCount - comment.replies.length;
      return hidden === 1 ? "View 1 reply" : `View ${hidden} replies`;
    },
    startReply(parent, answering) {
      if (!this.storyComments.requireSignedInReader()) {
        return;
      }
      if (this.replyInComposer) {
        this.$emit("reply-requested", { parent, answering });
        return;
      }
      this.replyError = "";
      this.replyingTo = parent.id;
      this.replyPrefill = answering.authorName ? replyPrefillSegments({ id: answering.authorId, label: answering.authorName }) : null;
      this.replyKey += 1;
    },
    stopReply() {
      this.replyingTo = 0;
      this.replyPrefill = null;
    },
    sendReply(parent, segments) {
      this.replyError = "";
      return this.storyComments.postReply(this.storyId, parent, segments).then(() => {
        this.stopReply();
      }, error => {
        if (error instanceof SentCommentNotEchoedError) {
          this.stopReply();
          return;
        }
        this.replyError = error.message;
        throw error;
      });
    },
  },
};
</script>
